import { useEffect, useState } from "react";
import { Chip, Stack, TextField, Typography } from "@mui/material";

const normalizeGenres = (genres: string[]) => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const genre of genres) {
    const trimmed = genre.trim();
    if (!trimmed) continue;

    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    normalized.push(trimmed);
  }

  return normalized;
};

export const parseGenres = (value?: string | null) => {
  if (!value) return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeGenres(parsed.map((item) => String(item)));
      }
    } catch {
      // fall through to delimiter parsing
    }
  }

  return normalizeGenres(trimmed.split(/[\n,;]+/));
};

export const formatGenres = (genres: string[]) => genres.join(", ");

type GenreInputProps = {
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export const GenreInput = ({
  value,
  onChange,
  disabled,
}: GenreInputProps) => {
  const [genreInput, setGenreInput] = useState("");
  const [genreList, setGenreList] = useState<string[]>(() =>
    parseGenres(value)
  );

  useEffect(() => {
    setGenreInput("");
    setGenreList(parseGenres(value));
  }, [value]);

  const updateGenres = (nextGenres: string[]) => {
    const normalized = normalizeGenres(nextGenres);
    setGenreList(normalized);
    onChange(formatGenres(normalized));
  };

  const handleAddGenres = (input: string) => {
    const parsed = parseGenres(input);
    if (parsed.length === 0) return;

    updateGenres([...genreList, ...parsed]);
    setGenreInput("");
  };

  const handleRemoveGenre = (genreToRemove: string) => {
    updateGenres(genreList.filter((genre) => genre !== genreToRemove));
  };

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        Genres
      </Typography>
      <Stack spacing={0.25}>
        <TextField
          id="band-genres"
          label="Add genre"
          variant="outlined"
          value={genreInput}
          onChange={(event) => setGenreInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            handleAddGenres(genreInput);
          }}
          fullWidth
          margin="dense"
          helperText="Press Enter to add a genre"
          disabled={disabled}
        />
        {genreList.length > 0 && (
          <div style={{marginTop: -8}}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {genreList.map((genre) => (
              <Chip
                key={genre}
                label={genre}
                onDelete={() => handleRemoveGenre(genre)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
          </div>
        )}
      </Stack>
    </Stack>
  );
};
